import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Cancel as CancelIcon,
  History as HistoryIcon,
} from "@mui/icons-material";

import { usePageTitle } from "../context/PageTitleContext";
import {
  useCancelTransferOrder,
  useTransferOrdersList,
} from "../hooks/useDistributionAnalytics";
import type { TransferOrderListItem } from "../services/distributionService";

const STATUS_COLOR: Record<
  TransferOrderListItem["status"],
  "default" | "info" | "success" | "warning"
> = {
  draft: "default",
  in_progress: "info",
  received: "success",
  cancelled: "warning",
};

const STALE_DRAFT_DAYS = 7;

function isStaleDraft(item: TransferOrderListItem): boolean {
  if (item.status !== "draft" && item.status !== "in_progress") return false;
  const ageMs = Date.now() - new Date(item.created_at).getTime();
  return ageMs > STALE_DRAFT_DAYS * 24 * 60 * 60 * 1000;
}

/**
 * B3.32 — TransferOrdersPage
 *
 * Histórico de transferencias creadas por MasXPieza con filtros y su
 * estado actual en Odoo (sincronizado vía polling-picking-state cada
 * 15 min). Solo lectura.
 */
export function TransferOrdersPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const [store, setStore] = useState("");
  const [sourceType, setSourceType] = useState<"" | TransferOrderListItem["source_type"]>("");
  const [status, setStatus] = useState<"" | TransferOrderListItem["status"]>("");
  // Bug fix 2026-05-08 — estado del dialog de confirmación de cancelación
  // y del feedback (snackbar) tras la mutation. cancelTarget guarda el
  // order completo para poder mostrar contexto en el dialog.
  const [cancelTarget, setCancelTarget] = useState<TransferOrderListItem | null>(
    null,
  );
  const [feedback, setFeedback] = useState<
    | { kind: "success"; recos: number }
    | { kind: "error"; msg: string }
    | null
  >(null);
  const cancelMutation = useCancelTransferOrder();

  useEffect(() => {
    setTitle("Distribución — Transferencias");
    return () => setTitle("");
  }, [setTitle]);

  const params: Parameters<typeof useTransferOrdersList>[0] = { limit: 100 };
  if (store) params.store = store.toUpperCase();
  if (sourceType) params.source_type = sourceType;
  if (status) params.status = status;
  const { data, isLoading } = useTransferOrdersList(params);

  const items = data?.items ?? [];

  async function handleConfirmCancel(): Promise<void> {
    if (!cancelTarget) return;
    try {
      const res = await cancelMutation.mutateAsync({
        id: cancelTarget.id,
        reason: "manual",
      });
      setFeedback({ kind: "success", recos: res.recos_reverted });
      setCancelTarget(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setFeedback({ kind: "error", msg });
    }
  }

  function canCancel(item: TransferOrderListItem): boolean {
    return item.status === "draft" || item.status === "in_progress";
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <HistoryIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Transferencias
          </Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField
              size="small"
              label="Tienda"
              value={store}
              onChange={(e) => setStore(e.target.value)}
              placeholder="SLP04"
            />
            <TextField
              size="small"
              select
              label="Fuente"
              value={sourceType}
              onChange={(e) =>
                setSourceType(e.target.value as "" | TransferOrderListItem["source_type"])
              }
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="seeding">seeding</MenuItem>
              <MenuItem value="stock_alert">stock_alert</MenuItem>
              <MenuItem value="reorder">reorder</MenuItem>
            </TextField>
            <TextField
              size="small"
              select
              label="Estado"
              value={status}
              onChange={(e) =>
                setStatus(e.target.value as "" | TransferOrderListItem["status"])
              }
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="draft">draft</MenuItem>
              <MenuItem value="in_progress">in_progress</MenuItem>
              <MenuItem value="received">received</MenuItem>
              <MenuItem value="cancelled">cancelled</MenuItem>
            </TextField>
          </Stack>
        </Paper>

        <Paper variant="outlined">
          {isLoading && (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}
          {!isLoading && items.length === 0 && (
            <Alert severity="info" sx={{ m: 2 }}>
              No hay transferencias para los filtros seleccionados.
            </Alert>
          )}
          {items.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Creado</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Destino</TableCell>
                    <TableCell>Fuente</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Odoo picking</TableCell>
                    <TableCell>Received</TableCell>
                    <TableCell>request_id</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((o) => {
                    const stale = isStaleDraft(o);
                    return (
                    <TableRow key={o.id} hover>
                      <TableCell>
                        {new Date(o.created_at).toLocaleString("es-MX")}
                      </TableCell>
                      <TableCell>{o.origin_warehouse}</TableCell>
                      <TableCell>{o.store_mxp_code}</TableCell>
                      <TableCell>{o.source_type}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Chip
                            size="small"
                            color={STATUS_COLOR[o.status]}
                            label={o.status}
                          />
                          {stale && (
                            <Chip
                              size="small"
                              color="error"
                              label={`>${STALE_DRAFT_DAYS}d`}
                              title={`Abierto hace más de ${STALE_DRAFT_DAYS} días sin ejecutar en Odoo`}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{o.odoo_picking_id ?? "—"}</TableCell>
                      <TableCell>
                        {o.received_at
                          ? new Date(o.received_at).toLocaleString("es-MX")
                          : "—"}
                      </TableCell>
                      <TableCell
                        sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                      >
                        {o.request_id}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={
                            canCancel(o)
                              ? "Cancelar transferencia y devolver recomendaciones al pool"
                              : `No se puede cancelar (estado: ${o.status})`
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={!canCancel(o)}
                              onClick={() => setCancelTarget(o)}
                              aria-label={`Cancelar transferencia ${o.id}`}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {data && (
            <Box sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Mostrando {items.length} de {data.total} transferencias.
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={cancelTarget !== null}
        onClose={() => !cancelMutation.isPending && setCancelTarget(null)}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">Cancelar transferencia</DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            {cancelTarget && (
              <>
                Cancelar la transferencia{" "}
                <strong>{cancelTarget.origin_warehouse}</strong> →{" "}
                <strong>{cancelTarget.store_mxp_code}</strong>
                {cancelTarget.odoo_picking_id !== null && (
                  <> (Odoo picking #{cancelTarget.odoo_picking_id})</>
                )}
                . Las recomendaciones asociadas volverán al pool pendiente y
                podrán incluirse en el próximo preview de distribución.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCancelTarget(null)}
            disabled={cancelMutation.isPending}
          >
            No, regresar
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? "Cancelando..." : "Sí, cancelar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback !== null}
        autoHideDuration={6000}
        onClose={() => setFeedback(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {feedback ? (
          <Alert
            onClose={() => setFeedback(null)}
            severity={feedback.kind === "success" ? "success" : "error"}
            sx={{ width: "100%" }}
          >
            {feedback.kind === "success"
              ? `Transferencia cancelada. ${feedback.recos} recomendación(es) regresaron al pool.`
              : `Error al cancelar: ${feedback.msg}`}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
}
